import React, { useState, forwardRef, useImperativeHandle, useMemo, useRef, useCallback } from 'react';
import clsx from 'clsx';
import SalesAllowanceItemTable from '@Local/Orders/Componetnets/SalesAllowanceItemTable/SalesAllowanceItemTable';
import useRefundSettlement from '@Local/Orders/Componetnets/RefundSettlement/useRefundSettlement';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@curves/ui-base';

import { formatCurrencyFn, isFunction, refIsRequiredError } from '@Util/utils';
import SalesAllowanceReadonlyTable from '@Local/Orders/Componetnets/SalesAllowanceItemTable/SalesAllowanceReadonlyTable';
import { Grid } from '@mui/material';
import { localUser } from '@Util/utils';
/**
 * 訂單折讓/作廢結算區。
 * - 顯示商品清單並計算應退金額
 * - 驗證退費金額與必填原因
 * - 送出整理後的 payload 給 onSubmit
 */
const RefundSettlement = forwardRef(({ className, mode = 'refund', groups = [], listDisabled = false, onSubmit, employeeID, isTriplicateUniformInvoice = false }, ref) => {
    const { userID } = localUser.get();
    const tableRefs = useRef({});
    const [tableTotals, setTableTotals] = useState({});
    const [tableErrors, setTableErrors] = useState({});
    const handleGroupTotalChange = useCallback(
        (key, total) => {
            const next = Number(total) || 0;
            setTableTotals((prev) => {
                if (prev[key] === next) return prev;
                return { ...prev, [key]: next };
            });
        },
        // eslint-disable-next-line
        [],
    );
    const handleGroupErrorChange = useCallback((key, hasError) => {
        const next = Boolean(hasError);
        setTableErrors((prev) => {
            if (prev[key] === next) return prev;
            return { ...prev, [key]: next };
        });
    }, []);
    const refundableTotal = useMemo(() => Object.values(tableTotals).reduce((a, b) => a + (Number(b) || 0), 0), [tableTotals]);
    const tablesHasError = useMemo(() => (mode === 'refund' ? Object.values(tableErrors).some(Boolean) : false), [tableErrors, mode]);
    const settlement = useRefundSettlement({ expectedTotal: refundableTotal, tablesHasError });
    const [cardTouched, setCardTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const showErrors = cardTouched || submitted;
    const notesRef = useRef(null);
    const getResult = useCallback(
        () => {
            const items = groups.flatMap((g) => tableRefs.current[g.key]?.getResult?.() || []);
            return {
                employeeID: userID ?? 0,
                cashRefund: Number(settlement.cashAmount) || 0,
                cardRefund: Number(settlement.cardAmount) || 0,
                items,
                notes: notesRef.current.getResult() || '',
            };
        },
        // eslint-disable-next-line
        [groups, employeeID, refundableTotal, settlement, userID],
    );
    useImperativeHandle(
        ref,
        () => ({
            getResult,
            isError: () => {
                let _isError = settlement.isError();
                if (!_isError) _isError = refIsRequiredError(notesRef);
                return _isError;
            },
            getTotal: () => refundableTotal,
        }),
        // eslint-disable-next-line
        [getResult, settlement],
    );
    const handleSubmit = () => {
        // 觸發顯示驗證訊息
        setSubmitted(true);
        const payload = getResult();
        if (!payload || settlement.isError()) return;
        isFunction(onSubmit) && onSubmit(payload);
    };
    return (
        <Box className={clsx('refund-settlement', className)}>
            {groups.map((g) => (
                <Box key={g.key} className="refund-group">
                    {g.title && (
                        <Typography variant="h6" className="mb-1">
                            {g.title}
                        </Typography>
                    )}
                    {mode === 'refund' ? (
                        <SalesAllowanceItemTable
                            ref={(el) => {
                                tableRefs.current[g.key] = el;
                            }}
                            className="mb-4"
                            items={g.items || []}
                            listDisabled={listDisabled}
                            onTotalChange={(total) => handleGroupTotalChange(g.key, total)}
                            onValidityChange={(hasError) => handleGroupErrorChange(g.key, hasError)}
                        />
                    ) : (
                        <SalesAllowanceReadonlyTable
                            ref={(el) => {
                                tableRefs.current[g.key] = el;
                            }}
                            className="mb-4"
                            items={g.items || []}
                            onTotalChange={(total) => handleGroupTotalChange(g.key, total)}
                            onValidityChange={(hasError) => handleGroupErrorChange(g.key, hasError)}
                        />
                    )}
                    <Divider className="my-4" />
                </Box>
            ))}
            {isTriplicateUniformInvoice === true ? (
                <Alert
                    className="mb-2 alert-justify-content-start"
                    color="info"
                    severity="info"
                    variant="outlined"
                >
                    此訂單開立三聯式發票，需請店舖教練通知會員完成「三聯發票折讓證明單」
                </Alert>
            ) : null}
            {/* 結算區 */}
            <Grid spacing={2} container>
                <Grid xs={4} item />
                <Grid xs={8} item>
                    <Box className="refund-summary">
                        <Stack alignItems="center" justifyContent="space-between" spacing={2} className={'mb-2'}>
                            <Typography variant="body1" className="white-space-nowrap mb-0">
                                應退費總計
                            </Typography>
                            <Typography variant="h6">{formatCurrencyFn(refundableTotal)}</Typography>
                        </Stack>
                        <Stack alignItems="center" justifyContent="space-between" spacing={2} className={'mb-2'}>
                            <Typography variant="body1" className="white-space-nowrap mb-0">
                                信用卡退費<span className={'text-error ml-1'}>*</span>
                            </Typography>
                            <TextField
                                maskType="MONEY"
                                value={settlement.cardAmount}
                                inputProps={{ min: 0 }}
                                style={{ minWidth: 320 }}
                                onChangeNative={(_, v) => {
                                    settlement.setCard(v);
                                    if (!cardTouched) setCardTouched(true);
                                }}
                                required
                            />
                        </Stack>
                        <Stack alignItems="center" justifyContent="space-between" spacing={2} className={'mb-2'}>
                            <Typography variant="body1" className="white-space-nowrap text-primary mb-0">
                                實際退費金額
                            </Typography>
                            <Typography variant="h6" className="font-weight-bold text-primary">
                                {formatCurrencyFn(settlement.actualRefund)}
                            </Typography>
                        </Stack>
                        {/* 錯誤訊息：延後顯示（有互動或按送出） */}
                        {showErrors &&
                            settlement.errors.length > 0 &&
                            settlement.errors.map((msg, idx) => (
                                <Alert key={idx} variant="outlined" severity="error" color="error" className="mb-2">
                                    {msg}
                                </Alert>
                            ))}
                        <Stack alignItems="start" justifyContent="space-between" spacing={2} className={'mb-4'}>
                            <Typography variant="body1" className="white-space-nowrap mb-0">
                                原因<span className={'text-error ml-1'}>*</span>
                            </Typography>
                            <TextField ref={notesRef} style={{ minWidth: 320 }} rows={3} InputProps={{ placeholder: '請輸入退費原因' }} multiline required />
                        </Stack>
                        <Stack alignItems="start" justifyContent="end" className={'pt-2'}>
                            <Button variant="contained" color="primary" disabled={settlement.errors.length > 0} onClick={handleSubmit}>
                                {mode === 'void' ? '確認作廢' : '確認退款'}
                            </Button>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
});
export default RefundSettlement;
